import IMessage = kendo.ui.IMessage;
import type { Nullable } from '@src/shared/types/common';

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

interface AiChatResponse {
    Answer: string;
    /** Не приходит, если обсуждение не меняло ТЗ. */
    Spec?: string;
}

interface TranscribeResponse {
    Text: string;
}

interface AssistantState {
    messages: ChatMessage[];
    spec: string;
}

const BOT_AUTHOR_ID = 'bot';
const BOT_AUTHOR_NAME = 'AI-ассистент';
const USER_AUTHOR_ID = 'user';

const RECORDING_LIMIT_MS = 10 * 60 * 1000;
const RECORDING_LIMIT_BYTES = 25 * 1024 * 1024;

/** Форматы записи по убыванию совместимости с распознаванием. */
const RECORDING_MIME_TYPES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg'
];

let _wnd: kendo.ui.Window | undefined;
let _specEditor: kendo.ui.Editor | undefined;
let _recordButton: kendo.ui.Button | undefined;
let _recordTimer!: JQuery;

const getWnd = () => _wnd ??= $('#wndAiAssistant').data('kendoWindow');
const getSpecEditor = () => _specEditor ??= $('#wndAiAssistant_edtSpec').data('kendoEditor');

const staticButtons = new Map<string, kendo.ui.Button>();

let chat: Nullable<kendo.ui.Chat> = null;
let targetEditor: Nullable<kendo.ui.Editor> = null;
/** Незавершённые диалоги по редакторам: закрытие окна не теряет прогресс, перезагрузка страницы — теряет. */
const assistantStates = new Map<kendo.ui.Editor, AssistantState>();
let chatMessages: ChatMessage[] = [];
let partialLoaded = false;

let mediaRecorder: Nullable<MediaRecorder> = null;
let mediaStream: Nullable<MediaStream> = null;
let recordingStartedAt = 0;
let recordingTimer: Nullable<number> = null;
let isRecording = false;
let isStartingRecording = false;
/** Растёт при отмене записи: старт, зависший на запросе разрешения, видит смену сессии и отпускает микрофон. */
let recordingSession = 0;
let isBusy = false;

function getButton(id: string): Nullable<kendo.ui.Button> {
    const cached = staticButtons.get(id);

    if (cached) {
        return cached;
    }

    const button: Nullable<kendo.ui.Button> = $(`#${id}`).data('kendoButton') ?? null;

    if (!button) {
        return null;
    }

    staticButtons.set(id, button);
    return button;
}

function getSpecText(): string {
    const editor = getSpecEditor();

    if (!editor) {
        return '';
    }

    // Клавиатура и стирание оставляют в contenteditable разметку (<p><br></p>), поэтому пустота — по тексту.
    if (!editor.body.textContent.trim()) {
        return '';
    }

    return editor.value();
}

function ensurePartialLoaded(callback: () => void) {
    if (partialLoaded) {
        callback();
        return;
    }

    window.lockElement(true);
    $.get('/LiteLlm/AiAssistantWindow', (html) => {
        $('#aiAssistantWindowContainer').html(html);
        partialLoaded = true;
        window.lockElement(false);
        callback();
    }).fail(() => {
        window.lockElement(false);
        window.swal.fire('Ошибка', 'Не удалось загрузить окно AI-ассистента', 'error');
    });
}

function updateControls() {
    const locked = isBusy || isRecording || isStartingRecording;

    if (chat) {
        window.lockElement(isBusy, chat.element);
        chat.element.find('textarea').prop('disabled', locked);
    }

    _recordButton?.enable(!isBusy && !isStartingRecording);
}

function appendBotMessage(text: string) {
    chat?.postMessage({ text, authorId: BOT_AUTHOR_ID, authorName: BOT_AUTHOR_NAME } as IMessage);
    chatMessages.push({ role: 'assistant', content: text });
}

function saveAssistantState() {
    const state = targetEditor ? assistantStates.get(targetEditor) : undefined;

    if (state) {
        state.spec = getSpecText();
    }
}

function sendMessage(userText: string) {
    chatMessages.push({ role: 'user', content: userText });

    isBusy = true;
    updateControls();

    window.ajaxPost(
        '/LiteLlm/Chat',
        {
            specText: getSpecText(),
            messages: chatMessages
        },
        (data: AiChatResponse) => {
            appendBotMessage(data.Answer);

            const spec = data.Spec ?? '';

            if (spec) {
                // Модель отдаёт ТЗ чистым текстом, а редактор и поле описания задачи работают с HTML.
                getSpecEditor()?.value(window.renderMarkdownToSafeHtml(spec));
            }
        },
        (err: string) => {
            chatMessages.pop();
            window.swal.fire('Ошибка', err || 'Не удалось получить ответ AI', 'error');
        },
        () => {
            isBusy = false;
            updateControls();
        }
    );
}

const onChatSendMessage = (e: kendo.ui.ISendMessageEventArgs) => {
    sendMessage((e.message?.text ?? '').trim());
};

function onApplyClick(): void {
    const specText = getSpecText();

    if (specText) {
        targetEditor?.value(specText);
    }

    getWnd()?.close();
}

function initChatWidget() {
    const chatEl = $('#wndAiAssistant_chat');

    chatEl.data('kendoChat')?.destroy();
    chatEl.kendoChat({
        sendMessage: onChatSendMessage,
        speechToText: false,
        fileAttachment: false,
        authorId: USER_AUTHOR_ID,
        messageBox: {
            endAffixTemplate: () => '<span id="wndAiAssistant_recordTimer" class="text-nowrap"></span><button id="wndAiAssistant_btRecord" type="button" class="k-button">Запись</button>'
        },
        messageContentTemplate: (ctx) => `<div class="k-chat-bubble-text">${kendo.htmlEncode(ctx.text)}</div>`
    } as kendo.ui.IChatOptions);

    chat = chatEl.data('kendoChat') ?? null;
    initRecordButton();
}

/**
 * Кнопка записи приходит из шаблона affix-области обычной разметкой, а Chat.destroy() очищает
 * свой элемент — поэтому виджет создаётся заново при каждой сборке чата, а не один раз при загрузке окна.
 */
function initRecordButton() {
    const recordButtonEl = $('#wndAiAssistant_btRecord');

    recordButtonEl.kendoButton({
        icon: 'microphone',
        size: 'small',
        rounded: 'full',
        fillMode: 'outline'
    });

    _recordButton = recordButtonEl.data('kendoButton');
    _recordTimer = $('#wndAiAssistant_recordTimer');

    _recordButton?.bind('click', onRecordClick);
    updateControls();
}

function renderSuggestions() {
    chat?.postMessage({
        text: 'Здравствуйте! Задайте вопрос, надиктуйте требования кнопкой «Запись» или введите их текстом.',
        authorId: BOT_AUTHOR_ID,
        authorName: BOT_AUTHOR_NAME
    } as IMessage);
}

function stopRecordTimer() {
    if (recordingTimer) {
        window.clearInterval(recordingTimer);
        recordingTimer = null;
    }
}

/** Единая точка сброса таймера при любом окончании записи: снимает интервал и возвращает отображение к нулю. */
function resetRecordTimer() {
    stopRecordTimer();
    _recordTimer.text('00:00');
}

function setRecordTimerText() {
    const totalSeconds = Math.floor((Date.now() - recordingStartedAt) / 1000);
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    _recordTimer.text(`${minutes}:${seconds}`);
}

function startRecordTimer() {
    recordingStartedAt = Date.now();
    setRecordTimerText();

    recordingTimer = window.setInterval(() => {
        if (Date.now() - recordingStartedAt >= RECORDING_LIMIT_MS) {
            window.swal.fire('Внимание', 'Достигнут предел записи 10 минут, запись остановлена', 'warning');
            stopRecording();
            return;
        }

        setRecordTimerText();
    }, 1000);
}

function setRecordButtonState(recording: boolean) {
    _recordButton?.element.find('.k-button-text').text(recording ? 'Стоп' : 'Запись');
}

function stopTracks(stream: Nullable<MediaStream>) {
    stream?.getTracks().forEach((track) => track.stop());
}

function cancelRecording() {
    // Смена сессии гасит старт, зависший на запросе разрешения: поток будет отпущен без записи.
    recordingSession++;
    isStartingRecording = false;
    resetRecordTimer();

    // Явный stop(): без него активный рекордер останавливает сам браузер по завершению треков, и onstop отмены не проверен.
    // Гейт на inactive — от записи, уже остановленной stopRecording(), но ещё не отдавшей onstop: там stop() бросает.
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
    }

    stopTracks(mediaStream);
    mediaStream = null;
    mediaRecorder = null;
    isRecording = false;
    setRecordButtonState(false);
    updateControls();
}

/** Останавливает запись; расшифровка и освобождение микрофона — в onstop. */
function stopRecording() {
    resetRecordTimer();
    isRecording = false;
    setRecordButtonState(false);
    updateControls();

    mediaRecorder?.stop();
}

/** Расширение файла записи: по нему сервер определяет формат аудио. */
function getAudioExtension(mimeType: string): string {
    if (mimeType.includes('mp4')) {
        return 'mp4';
    }

    if (mimeType.includes('ogg')) {
        return 'ogg';
    }

    return 'webm';
}

/** Кодирует запись в base64 порциями: spread по всему буферу переполняет стек вызовов. */
function toBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 8192;
    let binary = '';

    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
    }

    return btoa(binary);
}

async function transcribeRecording(blob: Blob, extension: string) {
    isBusy = true;
    updateControls();

    const audioBase64 = toBase64(await blob.arrayBuffer());

    window.ajaxPost(
        '/LiteLlm/Transcribe',
        {
            audioBase64,
            fileName: `upload.${extension}`
        },
        (data: TranscribeResponse) => {
            const transcript = data.Text.trim();

            // Порог приёмки: пустая или односимвольная расшифровка — это не распознавание.
            if (transcript.length < 2) {
                void window.swal.fire('Внимание', 'Не расслышал, попробуйте ещё раз', 'warning');
                return;
            }

            chat?.value(transcript);
        },
        (err: string) => {
            void window.swal.fire('Ошибка', err || 'Не удалось распознать запись', 'error');
        },
        () => {
            isBusy = false;
            updateControls();
        }
    );
}

async function startRecording() {
    if (isRecording || isStartingRecording) {
        return;
    }

    const session = recordingSession;
    const mimeType = RECORDING_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));

    isStartingRecording = true;
    updateControls();

    let stream: MediaStream;

    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    }
    catch (error) {
        if (session !== recordingSession) {
            return;
        }
        console.error(error);
        isStartingRecording = false;
        updateControls();
        void window.swal.fire('Ошибка', 'Не удалось получить доступ к микрофону', 'error');
        return;
    }

    if (session !== recordingSession) {
        // Запись отменили, пока висел запрос разрешения: поток уже выдан, но не нужен — гасим микрофон.
        // isStartingRecording и updateControls() не трогаем: ими владеет актуальный старт, а если его нет —
        // состояние уже сброшено в cancelRecording.
        stopTracks(stream);
        return;
    }

    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

    if (recorder.mimeType.includes('ogg')) {
        void window.swal.fire(
            'Внимание',
            'Распознавание не принимает формат OGG. Для диктовки нужен Chrome или Edge.',
            'warning'
        );
    }

    const chunks: Blob[] = [];

    recorder.ondataavailable = (e) => {
        chunks.push(e.data);
    };
    recorder.onstop = () => {
        stopTracks(stream);

        if (mediaRecorder !== recorder) {
            return;
        }

        mediaStream = null;
        mediaRecorder = null;
        isRecording = false;
        setRecordButtonState(false);
        resetRecordTimer();
        updateControls();

        const blob = new Blob(chunks, { type: recorder.mimeType });

        if (blob.size > RECORDING_LIMIT_BYTES) {
            void window.swal.fire('Внимание', 'Запись больше 25 МБ, попробуйте записать короче', 'warning');
            return;
        }

        void transcribeRecording(blob, getAudioExtension(recorder.mimeType));
    };

    mediaStream = stream;
    mediaRecorder = recorder;
    isStartingRecording = false;

    recorder.start();

    isRecording = true;
    setRecordButtonState(true);
    updateControls();
    startRecordTimer();
}

function onRecordClick() {
    if (isRecording) {
        stopRecording();
        return;
    }

    void startRecording();
}

/**
 * Незавершённые диалоги хранятся по редакторам, пока открыта страница: закрытие окна ассистента
 * не теряет ни переписку, ни ТЗ, а перезагрузка страницы начинает диалог заново.
 */
function openAiAssistantWindow(editor: kendo.ui.Editor) {
    ensurePartialLoaded(() => {
        if (targetEditor !== editor) {
            saveAssistantState();
            targetEditor = editor;

            let state = assistantStates.get(editor);

            if (!state) {
                state = { messages: [], spec: editor.value() };
                assistantStates.set(editor, state);
            }

            chatMessages = state.messages;
            getSpecEditor()?.value(state.spec);
            initChatWidget();

            if (chatMessages.length) {
                chatMessages.forEach(({ role, content }) => {
                    chat?.postMessage({
                        text: content,
                        authorId: role === 'user' ? USER_AUTHOR_ID : BOT_AUTHOR_ID,
                        authorName: role === 'assistant' ? BOT_AUTHOR_NAME : undefined
                    } as IMessage);
                });
            }
            else {
                renderSuggestions();
            }
        }

        getWnd()?.center().open();
    });
}

function handleAiAssistantClick({ sender }: { sender: kendo.ui.Button }) {
    const editorId = sender.element.attr('data-editor-id');

    if (editorId) {
        const editor = $(`#${editorId}`).data('kendoEditor');
        editor && openAiAssistantWindow(editor);
    }
}

/** Вызывается из script-блока partial окна после autoInitControls(). */
function initAiAssistantWindow() {
    getButton('wndAiAssistant_btApply')?.bind('click', onApplyClick);
    getButton('wndAiAssistant_btCancel')?.bind('click', () => getWnd()?.close());
    getWnd()?.bind('close', () => {
        cancelRecording();
        saveAssistantState();
    });
}

window.openAiAssistantWindow = openAiAssistantWindow;
window.handleAiAssistantClick = handleAiAssistantClick;
window.initAiAssistantWindow = initAiAssistantWindow;
